import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Swords, Bot } from "lucide-react";
import api, { TIER_META } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const login = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function BattlePublic() {
  const { token } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);

  const load = async () => { try { setB((await api.get(`/battles/public/${token}`)).data); } catch (e) { setErr(true); } };
  useEffect(() => { load(); }, [token]);

  const vote = async (side) => {
    if (!user) return login();
    try { const r = (await api.post(`/battles/public/${token}/vote`, { voted_for: side })).data; setB((prev) => ({ ...prev, votes: r.votes })); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (err) return <Shell><div className="text-center text-zinc-400 py-20" data-testid="battle-notfound">This battle isn't available.</div></Shell>;
  if (!b) return <Shell><div className="text-center text-zinc-400 py-20">Loading…</div></Shell>;

  const meta = TIER_META[b.tier] || TIER_META.savage;
  const votes = b.votes || { challenger: 0, opponent: 0 };
  const total = votes.challenger + votes.opponent;
  const chPct = total ? Math.round(100 * votes.challenger / total) : 50;
  const voted = !!votes.my_vote;

  const Side = ({ side }) => {
    const name = side === "challenger" ? b.challenger_name : b.opponent_name;
    const roast = side === "challenger" ? b.challenger_roast : b.opponent_roast;
    const handle = side === "challenger" ? b.challenger_roaster : b.opponent_roaster;
    const pct = side === "challenger" ? chPct : 100 - chPct;
    const leads = b.crowd_vote_result === side;
    return (
      <div data-testid={`battle-side-${side}`} className="rounded-xl border p-5" style={{ borderColor: leads ? meta.color : "rgba(255,255,255,0.1)", background: leads ? `${meta.color}12` : "rgba(24,24,27,0.6)" }}>
        <div className="font-heading font-bold text-xl">{name}</div>
        <p className="text-zinc-200 mt-2 text-sm">"{roast}"</p>
        <div className="text-xs text-brand mt-2">{handle}</div>
        <button data-testid={`battle-vote-${side}`} onClick={() => vote(side)} disabled={voted}
          className={`mt-4 w-full py-2.5 rounded-full font-bold text-sm border transition-colors ${votes.my_vote === side ? "text-black" : "text-white border-white/15 hover:bg-white/5"} disabled:opacity-70`}
          style={votes.my_vote === side ? { background: meta.color, borderColor: meta.color } : {}}>
          {voted ? `${pct}% · ${side === "challenger" ? votes.challenger : votes.opponent} votes` : "Got roasted harder 🔥"}
        </button>
      </div>
    );
  };

  return (
    <Shell>
      <div className="max-w-2xl mx-auto" data-testid="battle-public">
        <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-4" style={{ color: meta.color, background: `${meta.color}22` }}><Swords size={13} /> Roast-Off · {meta.label}</div>
        <h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl mb-2">Who got roasted harder?</h1>
        <p className="text-zinc-400 mb-6">Vote below. This poll is for bragging rights only — it never affects anyone's payout.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Side side="challenger" /><Side side="opponent" />
        </div>
        {b.ai_judge_result && (
          <div data-testid="battle-public-aijudge" className="mt-4 rounded-xl border border-[#a855f7]/30 bg-[#a855f7]/5 p-4">
            <div className="text-xs font-bold uppercase tracking-widest text-[#a855f7] flex items-center gap-1.5"><Bot size={13} /> AI Judge's verdict</div>
            <p className="text-sm text-zinc-200 mt-1">Winner: <b className="capitalize">{b.ai_judge_result.winner === "challenger" ? b.challenger_name : b.opponent_name}</b> — {b.ai_judge_result.reason}</p>
          </div>
        )}
        <div className="mt-12 pt-8 border-t border-white/10 text-center" data-testid="battle-your-turn">
          <div className="font-heading font-bold uppercase tracking-tighter text-2xl">Think you'd survive a roast?</div>
          <button data-testid="battle-cta" onClick={login} className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand hover:bg-brand-hover text-black font-bold">Start your own Roast-Off</button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen grain relative overflow-hidden">
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-brand/20 blur-[120px] pointer-events-none" />
      <header className="relative z-10 max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2"><Flame className="text-brand" size={24} /><span className="font-heading font-bold text-xl tracking-tighter">RoastLit</span></a>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-10">{children}</main>
    </div>
  );
}
